import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { useState, useCallback } from "react"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Persisted dismiss state for warning/info banners.
 *
 * Returns `[isDismissed, dismiss]` for the given `key`. The dismissed state
 * survives reloads via localStorage. To reset all dismissed banners (e.g.
 * a future Settings → "show all banners again" toggle), iterate localStorage
 * keys starting with the `LF_BANNER_DISMISS_PREFIX` constant.
 *
 * Use a unique, stable key per banner — e.g. `acquisition-date-mismatch:${projectId}`.
 */
export const LF_BANNER_DISMISS_PREFIX = "lf_banner_dismissed:"

export function useDismissedBanner(key) {
  const storageKey = `${LF_BANNER_DISMISS_PREFIX}${key}`
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === "true" } catch { return false }
  })
  const dismiss = useCallback(() => {
    setDismissed(true)
    try { localStorage.setItem(storageKey, "true") } catch { /* noop */ }
  }, [storageKey])
  return [dismissed, dismiss]
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCurrencyDetailed(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Format a numeric value for display in input fields (adds commas, no $ sign). */
export function fmtInputVal(v) {
  if (v === '' || v === null || v === undefined) return '';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n) || n === 0) return '';
  return n.toLocaleString('en-US');
}

/**
 * Drop subdivided parents from a project list so a sum() of `purchase_price`
 * doesn't double-count (parent's $X + each child's allocated cost basis).
 *
 * Apply at any aggregation that totals dollars across project rows.
 * Don't apply when counting projects / showing pipeline status — those need
 * parent + children kept separate.
 */
export function dedupeSubdividedParents(projects) {
  if (!Array.isArray(projects)) return [];
  const parentIdsWithChildrenInList = new Set(
    projects.filter(p => p && p.parent_project_id).map(p => p.parent_project_id)
  );
  return projects.filter(p => !parentIdsWithChildrenInList.has(p.id));
}

/** Strip non-numeric characters from input value (keeps digits and decimal point). */
export function stripToNum(v) {
  return String(v || '').replace(/[^0-9.]/g, '');
}

/**
 * Format an acreage value for display — always 2 decimal places.
 * Returns '-' when value is null/undefined/blank.
 * Examples: fmtAcres(12.345) -> '12.35'; fmtAcres(40) -> '40.00'
 */
export function fmtAcres(v) {
  if (v === '' || v === null || v === undefined) return '-';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!isFinite(n)) return '-';
  return n.toFixed(2);
}


/**
 * Get responsive font size class based on value magnitude
 * Larger values get smaller fonts to fit in containers
 */
export function getResponsiveFontSize(amount, baseSize = 'xl') {
  if (amount === null || amount === undefined) return `text-${baseSize}`;
  const absAmount = Math.abs(amount);
  
  // Map base sizes to their responsive variants
  const sizeMap = {
    '3xl': { large: 'text-3xl', medium: 'text-2xl', small: 'text-xl', tiny: 'text-lg' },
    '2xl': { large: 'text-2xl', medium: 'text-xl', small: 'text-lg', tiny: 'text-base' },
    'xl': { large: 'text-xl', medium: 'text-lg', small: 'text-base', tiny: 'text-sm' },
    'lg': { large: 'text-lg', medium: 'text-base', small: 'text-sm', tiny: 'text-xs' },
  };
  
  const sizes = sizeMap[baseSize] || sizeMap['xl'];
  
  if (absAmount >= 100000000) return sizes.tiny;      // $100M+
  if (absAmount >= 10000000) return sizes.small;      // $10M+
  if (absAmount >= 1000000) return sizes.medium;      // $1M+
  return sizes.large;
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
  if (dateString.length === 10 && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); // Local time
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateShort(dateString) {
  if (!dateString) return '-';
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
  if (dateString.length === 10 && dateString.includes('-')) {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); // Local time
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
}

export function calculateDaysHeld(acquisitionDate) {
  if (!acquisitionDate) return 0;
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone shift
  let acq;
  if (acquisitionDate.length === 10 && acquisitionDate.includes('-')) {
    const [year, month, day] = acquisitionDate.split('-');
    acq = new Date(year, month - 1, day);
  } else {
    acq = new Date(acquisitionDate);
  }
  const now = new Date();
  const diffTime = Math.abs(now - acq);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getStatusColor(status) {
  const colors = {
    'Lead': 'bg-slate-100 text-slate-700 border-slate-300',
    'LOI': 'bg-blue-100 text-blue-700 border-blue-300',
    'Under_Contract': 'bg-amber-100 text-amber-700 border-amber-300',
    'Owned': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Subdividing': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Listed': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Under_Contract_Sell': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Sold': 'bg-green-100 text-green-800 border-green-400',
    'Completed': 'bg-slate-200 text-slate-800 border-slate-500',
    'Note_Held': 'bg-green-100 text-green-800 border-green-400',
    'Dead': 'bg-red-100 text-red-700 border-red-400',
  };
  return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
}

// Returns hierarchical status display: primary tag + optional sub-tag
export function getStatusDisplay(status) {
  const OWNED_SUBSTATES = {
    'Subdividing': 'Subdividing',
    'Listed': 'Listed',
    'Under_Contract_Sell': 'Under Contract (Sell)',
  };
  const subColors = {
    'Subdividing': 'bg-purple-100 text-purple-700 border-purple-300',
    'Listed': 'bg-cyan-100 text-cyan-700 border-cyan-300',
    'Under_Contract_Sell': 'bg-teal-100 text-teal-700 border-teal-300',
    'Note_Held': 'bg-orange-100 text-orange-700 border-orange-300',
  };

  if (OWNED_SUBSTATES[status]) {
    return {
      primary: 'Owned',
      primaryColor: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      sub: OWNED_SUBSTATES[status],
      subColor: subColors[status],
    };
  }
  if (status === 'Note_Held') {
    return {
      primary: 'Sold',
      primaryColor: 'bg-green-100 text-green-800 border-green-400',
      sub: 'Note Held',
      subColor: subColors['Note_Held'],
    };
  }
  if (status === 'Completed') {
    return {
      primary: 'Completed',
      primaryColor: 'bg-slate-200 text-slate-800 border-slate-500',
      sub: 'Finalized',
      subColor: 'bg-slate-100 text-slate-600 border-slate-300',
    };
  }
  // No sub-tag for these
  return {
    primary: (status || '').replace(/_/g, ' '),
    primaryColor: getStatusColor(status),
    sub: null,
    subColor: null,
  };
}

export function getAgingLevel(daysHeld) {
  if (daysHeld > 300) return 'critical';
  if (daysHeld > 200) return 'warning';
  return 'normal';
}

export const PROJECT_STATUSES = [
  'Under_Contract',
  'Owned',
  'Subdividing',
  'Listed',
  'Under_Contract_Sell',
  'Sold',
  'Completed',
  'Note_Held',
  'Dead',
];

// Wave-4 follow-up: simplified Type list. Only the 6 below appear in the
// Add-Transaction dropdown. The rest are kept as enum values for backward
// compat with existing data + dedicated flows (Notes Receivable page,
// QBO importer, etc.) but are filtered out of the user-facing picker.
//
// Visible (6):
//   Expense / EMD / Purchase Price / Reimbursement / Refund (Dead Deal) / Investor Wire
// Hidden (still valid in DB):
//   Capital_In, Sale_Revenue, Pre_Sale_Payment, Vendor_Reimbursement,
//   Note_Payment, Manager_Advance, Investor_Advance
export const TRANSACTION_TYPES = [
  { value: 'Expense', label: 'Expense', color: 'text-red-600', visible: true },
  { value: 'EMD', label: 'Earnest Money Deposit (EMD)', color: 'text-blue-600', visible: true },
  { value: 'Purchase_Price', label: 'Purchase Price', color: 'text-orange-600', visible: true },
  { value: 'Reimbursement', label: 'Reimbursement', color: 'text-emerald-600', visible: true },
  { value: 'Refund', label: 'Refund (Dead Deal)', color: 'text-cyan-600', visible: true },
  { value: 'Investor_Contribution', label: 'Investor Wire (Capital Contribution)', color: 'text-indigo-600', visible: true, cashPosition: true },
  // Hidden — kept for backward compat & dedicated flows
  { value: 'Capital_In', label: 'Capital In', color: 'text-blue-600', visible: false },
  { value: 'Sale_Revenue', label: 'Sale Revenue', color: 'text-green-600', visible: false },
  { value: 'Pre_Sale_Payment', label: 'Pre-Sale Payment (EMD/DD from Buyer)', color: 'text-teal-600', visible: false },
  { value: 'Vendor_Reimbursement', label: 'Vendor Reimbursement', color: 'text-emerald-600', visible: false },
  { value: 'Note_Payment', label: 'Note Payment', color: 'text-purple-600', visible: false },
  { value: 'Manager_Advance', label: 'Manager Advance (LF fronted, investor owes)', color: 'text-amber-600', visible: false, cashPosition: true },
  { value: 'Investor_Advance', label: 'Investor Advance (over-paid, LF owes investor)', color: 'text-cyan-600', visible: false, cashPosition: true },
];

// User-facing list (the dropdown filters to this).
export const TRANSACTION_TYPES_VISIBLE = TRANSACTION_TYPES.filter(t => t.visible);

export const RESPONSIBLE_PARTIES = [
  { value: 'Investor', label: 'Investor/Partner' },
  { value: 'Land_Finder', label: 'Land Finder' },
];

export const FUNDING_SOURCES = [
  { value: 'Investor', label: 'Investor/Partner Funded' },
  { value: 'Land_Finder', label: 'Land Finder Funded' },
];

// ── BUCKET-GROUPED TRANSACTION CATEGORIES (Wave 3) ───────────────────────
// Categories are now organized by which JV split bucket they belong to.
// When the user picks a category, the bucket is automatically set —
// they no longer have to think about it twice.
//
// The structure:
//   { bucket: 'purchase'|'closing'|'dd'|'improvements'|'selling'|'note'|'other',
//     label:  human-readable bucket label,
//     items:  array of category strings under this bucket }
//
// The buckets that map to JV splits (purchase/closing/dd/improvements):
//   the JV's bucket-split % determines who's responsible for that line item.
// Selling bucket comes off the top of the SOP waterfall (no responsibility).
// Note/Other are non-bucketed (don't enter responsibility math).
export const TRANSACTION_CATEGORY_GROUPS = [
  {
    bucket: 'purchase',
    label: 'Purchase',
    description: 'Purchase price + EMD. JV.purchase_split % allocates responsibility.',
    items: [
      'Purchase Price',
      'Earnest Money Deposit',
      'Down Payment',
    ],
  },
  {
    bucket: 'closing',
    label: 'Closing Costs (Buy-Side)',
    description: 'Settlement-side costs at HUD. JV.closing_split % allocates responsibility.',
    items: [
      'Title Insurance',
      'Title Search / Commitment',
      'Closing Attorney Fee',
      'Settlement / Escrow Fee',
      'Recording Fees',
      'Notary Fees',
      'Wire / Bank Fees (Closing)',
      'Property Tax Proration (Buy)',
      'HOA Dues Proration',
      'Lender Fees / Loan Origination',
      'Other Closing Cost',
    ],
  },
  {
    bucket: 'dd',
    label: 'Due Diligence',
    description: 'Pre-acquisition investigation. JV.dd_split % allocates responsibility.',
    items: [
      'Survey',
      'Soil Test',
      'Environmental Study (Phase I/II)',
      'Wetlands Delineation',
      'Engineering / Civil',
      'Appraisal',
      'Property Inspection',
      'DD Legal Fees',
      'DD Bank / Wire Fees',
      'Other Due Diligence',
    ],
  },
  {
    bucket: 'improvements',
    label: 'Improvements / WIP',
    description: 'Land improvements during ownership. JV.improvements_split % allocates responsibility.',
    items: [
      'Clearing & Grading',
      'Septic / Well',
      'Driveway / Road',
      'Utilities (Water/Sewer/Power Tap)',
      'Subdivision Recording',
      'Plat Filing / Engineering',
      'Permits',
      'Fence / Gate',
      'Improvement Legal',
      'Maintenance',
      'Property Tax (During Hold)',
      'Insurance (During Hold)',
      'HOA Dues (During Hold)',
      'Other Improvement',
    ],
  },
  {
    bucket: 'selling',
    label: 'Selling Expenses (Sell-Side)',
    description: 'Sale-side costs — come off the top of the SOP waterfall (no responsibility split).',
    items: [
      'Real Estate Commission (Listing)',
      'Real Estate Commission (Buyer-Side)',
      'Marketing / Photography',
      'Drone Services',
      'Sale-Side Legal',
      'Sale-Side Title Insurance',
      'Transfer Tax (Sell)',
      'Property Tax Proration (Sell)',
      'Other Selling Cost',
    ],
  },
  {
    bucket: 'note',
    label: 'Note Payments',
    description: 'Note payment splits — for projects sold on a note.',
    items: [
      'Note Payment - Principal',
      'Note Payment - Interest',
    ],
  },
  {
    bucket: 'emergency',
    label: 'Emergency Advance (Senior)',
    description: 'LF-fronted emergency outlays (well repair, lien payoff) that aren\'t anyone\'s contractual responsibility. At sale, LF is repaid SENIOR to the investor hurdle.',
    items: [
      'Emergency Manager-Funded Invoice',
      'Emergency Repair',
      'Lien Payoff',
      'Emergency Legal',
    ],
  },
  {
    bucket: '',
    label: 'Other',
    description: 'Catch-all — not bucketed (stays out of responsibility math).',
    items: [
      'Investor Contribution',
      'Manager Advance',
      'Miscellaneous',
    ],
  },
];

// Flat list of all category strings (back-compat — many places reference
// TRANSACTION_CATEGORIES expecting a flat string array).
export const TRANSACTION_CATEGORIES = TRANSACTION_CATEGORY_GROUPS.flatMap(g => g.items);

// Reverse-mapping: category string → bucket id. Used by TransactionFormDialog
// to auto-set the bucket when the user picks a category. O(1) lookup.
export const CATEGORY_TO_BUCKET_MAP = (() => {
  const map = {};
  for (const group of TRANSACTION_CATEGORY_GROUPS) {
    for (const cat of group.items) {
      map[cat] = group.bucket;
    }
  }
  return map;
})();

// ── LEGACY CATEGORY ALIASES ─────────────────────────────────────────────
// Map old free-form category names users may have entered before Wave 3 to
// their bucket-correct equivalents. Used at form-edit time so admins editing
// a legacy txn see the canonical category + bucket auto-populated.
// (Database-level back-fill is handled separately by an admin migration.)
export const LEGACY_CATEGORY_ALIASES = {
  'Closing Costs': 'Other Closing Cost',
  'Due Diligence': 'Other Due Diligence',
  'Improvements': 'Other Improvement',
  'Bank Fees': 'Wire / Bank Fees (Closing)',
  'Real Estate Commission': 'Real Estate Commission (Listing)',
  'Transfer Tax': 'Transfer Tax (Sell)',
  'Selling Closing Costs': 'Other Selling Cost',
  'Legal': 'DD Legal Fees',
  'Clearing': 'Clearing & Grading',
  'Utilities': 'Utilities (Water/Sewer/Power Tap)',
  'Insurance': 'Insurance (During Hold)',
  'Taxes': 'Property Tax (During Hold)',
};

export const aliasLegacyCategory = (cat) => LEGACY_CATEGORY_ALIASES[cat] || cat;
